import { useTranslation } from 'react-i18next'

import { SubpageFoodCostHeader } from '@/components/food-cost/SubpageFoodCostHeader'
import { listRoutableLearnArticles } from '@/features/aprender/articleBodies'
import { learnArticlePath } from '@/features/aprender/articles'
import { LEARN_TOPIC_TABS } from '@/features/aprender/learnTopicTabs'
import { LocaleLink } from '@/navigation/LocaleLink'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { ROUTES } from '@/navigation/constants'
import { absoluteLocalizedUrl } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { NewsletterSubscribe } from '@/features/newsletter/NewsletterSubscribe'
import { articleHub } from '@/shared/lib/articleHubStyles'
import { DEFAULT_SHARE_IMAGE } from '@/shared/lib/siteSeo'
import { ArticleHubListing } from '@/shared/ui/ArticleHubListing'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import { SocialShareMeta } from '@/shared/ui/SocialShareMeta'
import '@/styles/food-cost-fonts.css'

export function LearnPage() {
    const { t, i18n } = useTranslation()
    const locale = useLandingLocale()
    const articles = listRoutableLearnArticles(locale)
    const isEn = locale === 'en'

    const title = t('learnPage.meta.title', 'Aprende con Tipi | Gestión para hostelería')
    const description = t(
        'learnPage.meta.description',
        'Guías prácticas de escandallos, food cost, control de costes y emprender en hostelería.',
    )
    const canonicalUrl = absoluteLocalizedUrl(ROUTES.learn, locale)

    return (
        <div className={articleHub.page}>
            <LocaleSeoLinks path={ROUTES.learn} locale={locale} />
            <SocialShareMeta
                title={title}
                description={description}
                imageUrl={DEFAULT_SHARE_IMAGE}
                url={canonicalUrl}
            />
            <PageHelmet locale={locale}>
                <title>{title}</title>
                <meta name='description' content={description} />
                <link
                    rel='preload'
                    href='/fonts/Archivo/archivo-latin.woff2'
                    as='font'
                    type='font/woff2'
                    crossOrigin='anonymous'
                />
            </PageHelmet>

            <SubpageFoodCostHeader />

            <main className={articleHub.main}>
                <div className={articleHub.header}>
                    <div className={articleHub.headerCopy}>
                        <div className={articleHub.eyebrow}>{t('learnPage.eyebrow')}</div>
                        <h1 className={articleHub.title}>{t('learnPage.title')}</h1>
                    </div>
                    <p className={articleHub.intro}>{t('learnPage.intro')}</p>
                </div>

                <nav className={articleHub.tabs}>
                    {LEARN_TOPIC_TABS.map(tab => {
                        const label = isEn ? tab.labelEn : tab.labelEs
                        const isActive = tab.key === 'all'
                        return (
                            <LocaleLink
                                key={tab.key}
                                to={tab.to}
                                className={isActive ? articleHub.tabActive : articleHub.tabInactive}
                                aria-current={isActive ? 'page' : undefined}
                            >
                                {label}
                            </LocaleLink>
                        )
                    })}
                </nav>

                <ArticleHubListing
                    articles={articles}
                    articlePath={learnArticlePath}
                    locale={i18n.language}
                    readingMinutesLabel={count =>
                        t('learnPage.readingMinutes', {
                            count,
                            defaultValue: `${count} min de lectura`,
                        })
                    }
                    readArticleLabel={t('learnPage.readArticle')}
                />
            </main>

            <NewsletterSubscribe variant='hub' hub='aprender' source='hub/aprender' />

            <Footer landingPage='learn' className='border-article-cream/10 bg-article-shell' />
        </div>
    )
}
